import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Navbar from '../components/navbar/navbar'
import Footer from '../components/footer'
import { toast } from 'react-toastify';
import { API_BASE } from '../utils/api';

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
}

const PROPERTY_TYPES = [
    'Residential',
    'Commercial',
    'PG/Co-living',
    'Plot',
    'Land',
    'Bank Auction'
];

const SUB_TYPES: Record<string, string[]> = {
    'Residential': ['Apartment', 'Villa', 'Independent House', 'Penthouse', 'Studio', 'Duplex'],
    'Commercial': ['Office Space', 'Shop / Showroom', 'Warehouse', 'Co-working Space'],
    'PG/Co-living': ['PG for Boys', 'PG for Girls', 'Co-living Space'],
    'Plot': ['Residential Plot', 'Commercial Plot'],
    'Land': ['Agricultural Land', 'Industrial Land'],
    'Bank Auction': ['Residential', 'Commercial', 'Industrial'],
};

export default function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        purpose: 'Buy',
        propertyType: 'Residential',
        propertySubType: 'Apartment',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name === 'propertyType') {
            setFormData({
                ...formData,
                propertyType: value,
                propertySubType: SUB_TYPES[value][0] || ''
            });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.phone || !formData.message) {
            toast.error("Please fill in all required fields.");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'Common Inquiry',
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    purpose: formData.purpose,
                    propertyType: formData.propertyType,
                    propertySubType: formData.propertySubType,
                    message: `[Subject: ${formData.subject}] ${formData.message}`
                })
            });

            if (response.ok) {
                setSubmitted(true);
                toast.success("Message sent successfully!");
                setTimeout(() => setSubmitted(false), 5000);
                setFormData({ 
                    name: '', 
                    email: '', 
                    phone: '',
                    subject: '', 
                    purpose: 'Buy',
                    propertyType: 'Residential',
                    propertySubType: 'Apartment',
                    message: '' 
                });
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || "Failed to send message.");
            }
        } catch (error) {
            console.error("Contact form error:", error);
            toast.error("An error occurred. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: 'fa-phone-volume',
            title: 'Call Us',
            detail: '+91 95370 02666',
            subDetail: 'Mon-Sat: 10AM - 7PM',
            link: 'tel:+919537002666',
            color: 'bg-blue-50'
        },
        {
            icon: 'fa-envelope-open-text',
            title: 'Email Us',
            detail: 'info@mypropertyguru.in',
            subDetail: 'Expect a reply in 24h',
            link: 'mailto:info@mypropertyguru.in',
            color: 'bg-amber-50'
        },
        {
            icon: 'fa-location-dot',
            title: 'Our Office',
            detail: 'Gota, Ahmedabad',
            subDetail: 'Gujarat, India 382470',
            link: 'https://maps.google.com/?q=Money+Plant+High+Street,Gota,Ahmedabad',
            color: 'bg-emerald-50'
        }
    ];

    return (
        <div className="bg-white min-h-screen font-['Figtree'] selection:bg-accent/30">
            <Navbar transparent={false} />

            {/* Premium Hero Section */}
            <div className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 bg-[#05144b] overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-accent/10 rounded-full blur-[100px] animate-pulse"></div>
                    <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-blue-400/5 rounded-full blur-[100px]"></div>
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <span className="inline-block py-1.5 px-4 rounded-full bg-accent/20 border border-accent/30 text-accent text-xs font-black uppercase tracking-[0.3em] mb-6">
                            Contact Us
                        </span>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-8 tracking-tight">
                            Let's Talk About Your <br className="hidden md:block" />
                            <span className="text-accent underline decoration-accent/30 underline-offset-8">Property Goals</span>
                        </h1>
                        <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-medium">
                            Whether you're looking to buy your dream home or list a premium asset, our consultants are ready to provide expert guidance.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 -mt-16 relative z-20 pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Left Column: Contact Info & Map */}
                    <div className="lg:col-span-5 space-y-8">
                        {/* Info Cards Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                            {contactInfo.map((item, idx) => (
                                <motion.a
                                    key={idx}
                                    href={item.link}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="group block bg-white p-6 rounded-2xl border border-gray-100 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] hover:shadow-2xl transition-all duration-300"
                                >
                                    <div className="flex items-center gap-5">
                                        <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center text-2xl transition-transform group-hover:scale-110 duration-300`}>
                                            <i className={`fa-solid ${item.icon} text-primary`}></i>
                                        </div>
                                        <div>
                                            <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-1">{item.title}</h4>
                                            <div className="text-lg font-black text-primary group-hover:text-accent transition-colors">{item.detail}</div>
                                            <div className="text-xs font-bold text-gray-500 mt-0.5">{item.subDetail}</div>
                                        </div>
                                    </div>
                                </motion.a>
                            ))}
                        </div>

                        {/* Interactive Map */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-gray-100 rounded-3xl overflow-hidden shadow-inner h-[350px] relative group border-4 border-white"
                        >
                            <iframe 
                                title="Office Location"
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14674.801646271926!2d72.52924469999999!3d23.11161245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e8306020521e1%3A0xc6cb5222c5e52c78!2sMoney%20Plant%20High%20Street%2C%20Gota%2C%20Ahmedabad!5e0!3m2!1sen!2sin!4v1713370000000!5m2!1sen!2sin" 
                                width="100%" height="100%" style={{ border: 0 }} allowFullScreen={true} loading="lazy" className="grayscale group-hover:grayscale-0 transition-all duration-1000"
                            ></iframe>
                            <div className="absolute top-4 left-4">
                                <a 
                                    href="https://maps.google.com/?q=Money+Plant+High+Street,Gota,Ahmedabad" 
                                    target="_blank" rel="noreferrer"
                                    className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest text-primary shadow-lg border border-white/20 flex items-center gap-2 hover:bg-white transition-all"
                                >
                                    <i className="fa-solid fa-map-location-dot"></i> Open Maps
                                </a>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column: Inquiry Form */}
                    <div className="lg:col-span-7">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white p-8 md:p-12 rounded-[2.5rem] border border-gray-100 shadow-[0_30px_100px_-20px_rgba(5,20,75,0.08)] relative overflow-hidden"
                        >
                            {/* Form Header */}
                            <div className="mb-12">
                                <h2 className="text-3xl md:text-4xl font-black text-primary mb-4 tracking-tight">
                                    Send a <span className="italic text-accent">Message</span>
                                </h2>
                                <p className="text-gray-500 font-medium">Fill out the form below and we'll connect with you shortly.</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Full Name</label>
                                        <input 
                                            name="name" type="text" 
                                            className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary placeholder:text-gray-300"
                                            placeholder="Enter your name"
                                            value={formData.name} onChange={handleChange} required 
                                        />
                                    </div>
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Email Address</label>
                                        <input 
                                            name="email" type="email" 
                                            className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary placeholder:text-gray-300"
                                            placeholder="Enter your email"
                                            value={formData.email} onChange={handleChange} required 
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Phone Number</label>
                                        <input 
                                            name="phone" type="tel" 
                                            className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary placeholder:text-gray-300"
                                            placeholder="Enter phone number"
                                            value={formData.phone} onChange={handleChange} required 
                                        />
                                    </div>
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Subject</label>
                                        <input 
                                            name="subject" type="text" 
                                            className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary placeholder:text-gray-300"
                                            placeholder="What's this about?"
                                            value={formData.subject} onChange={handleChange} 
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Purpose</label>
                                        <div className="relative">
                                            <select 
                                                name="purpose"
                                                className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary appearance-none cursor-pointer"
                                                value={formData.purpose} onChange={handleChange}
                                            >
                                                <option value="Buy">Buy</option>
                                                <option value="Sell">Sell</option>
                                            </select>
                                            <i className="fa-solid fa-chevron-down absolute right-6 top-1/2 -translate-y-1/2 text-primary/30 pointer-events-none"></i>
                                        </div>
                                    </div>
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Property Type</label>
                                        <div className="relative">
                                            <select 
                                                name="propertyType"
                                                className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary appearance-none cursor-pointer"
                                                value={formData.propertyType} onChange={handleChange}
                                            >
                                                {PROPERTY_TYPES.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                            <i className="fa-solid fa-chevron-down absolute right-6 top-1/2 -translate-y-1/2 text-primary/30 pointer-events-none"></i>
                                        </div>
                                    </div>
                                    <div className="space-y-2 group">
                                        <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Sub Type</label>
                                        <div className="relative">
                                            <select 
                                                name="propertySubType"
                                                className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary appearance-none cursor-pointer"
                                                value={formData.propertySubType} onChange={handleChange}
                                            >
                                                {SUB_TYPES[formData.propertyType]?.map(sub => (
                                                    <option key={sub} value={sub}>{sub}</option>
                                                ))}
                                            </select>
                                            <i className="fa-solid fa-chevron-down absolute right-6 top-1/2 -translate-y-1/2 text-primary/30 pointer-events-none"></i>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2 group">
                                    <label className="text-[11px] font-black text-primary uppercase tracking-widest px-1">Your Message</label>
                                    <textarea 
                                        name="message" 
                                        className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-accent outline-none transition-all font-bold text-primary placeholder:text-gray-300 min-h-[150px] resize-none"
                                        placeholder="How can we help you?"
                                        value={formData.message} onChange={handleChange} required 
                                    ></textarea>
                                </div>

                                <div className="pt-4">
                                    <button 
                                        type="submit" 
                                        disabled={loading}
                                        className="w-full md:w-auto px-10 py-5 bg-primary hover:bg-accent text-white hover:text-primary rounded-2xl font-black text-sm uppercase tracking-[0.2em] transition-all duration-300 shadow-xl shadow-primary/20 hover:shadow-accent/30 flex items-center justify-center gap-3 group disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                                        ) : (
                                            <>
                                                Send Message 
                                                <i className="fa-solid fa-paper-plane group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Success Overlay */}
                            <AnimatePresence>
                                {submitted && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="absolute inset-0 bg-white/95 backdrop-blur-md z-50 flex flex-col items-center justify-center text-center p-8"
                                    >
                                        <div className="w-24 h-24 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center text-4xl mb-6 shadow-lg shadow-emerald-500/10">
                                            <i className="fa-solid fa-check"></i>
                                        </div>
                                        <h3 className="text-3xl font-black text-primary mb-2">Thank You!</h3>
                                        <p className="text-gray-500 font-bold max-w-xs">Your inquiry has been sent. Our team will contact you within 24 hours.</p>
                                        <button 
                                            onClick={() => setSubmitted(false)}
                                            className="mt-8 text-primary font-black uppercase text-[10px] tracking-widest hover:text-accent transition-colors"
                                        >
                                            Send another message
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Social Proof / Trust Section */}
            <div className="bg-gray-50 py-24 border-t border-gray-100">
                <div className="container mx-auto px-4 text-center">
                    <h3 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.5em] mb-12">Connect With Us</h3>
                    <div className="flex flex-wrap justify-center gap-4 md:gap-8">
                        {[
                            { icon: 'fa-facebook-f', link: 'https://www.facebook.com/profile.php?id=100078325324646' },
                            { icon: 'fa-instagram', link: 'https://www.instagram.com/mypropertyguru?igsh=aHc5NnhlN3MzdWFl&utm_source=qr' },
                            { icon: 'fa-linkedin-in', link: 'https://www.linkedin.com/in/my-property-guru/' },
                            { icon: 'fa-youtube', link: 'https://www.youtube.com/@mypropertyguru' },
                            { icon: 'fa-twitter', link: 'https://x.com/Mypropertyguru1' }
                        ].map((social, idx) => (
                            <motion.a
                                key={idx}
                                href={social.link}
                                target="_blank" rel="noreferrer"
                                whileHover={{ y: -5 }}
                                className="w-16 h-16 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-primary text-xl shadow-sm hover:shadow-xl hover:border-accent hover:text-accent transition-all duration-300"
                            >
                                <i className={`fa-brands ${social.icon}`}></i>
                            </motion.a>
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
